<template>
  <div class="relative flex w-full items-center">
    <label
      v-if="props.prefix"
      class="float-left mb-0 box-border flex items-center rounded-l-lg border border-r-0 border-solid border-border bg-card p-2.5 py-1.5 font-mono text-sm transition-colors"
    >
      <span>
        {{ props.prefix }}
      </span>
    </label>
    <input
      :id="id ?? settingId"
      ref="input"
      v-model="modelValue"
      :class="{
        'peer block w-full rounded-lg border border-solid border-border p-2.5 py-1.5 text-sm text-gray-900 ring-0 focus:border-blue-500 dark:bg-input dark:text-white dark:placeholder:text-muted-foreground': true,
        'design-system-input': state?.status === 'pending' || !state,
        'border-red-300 bg-red-50': state?.status === 'error',
        'border-green-300 bg-green-50': state?.status === 'success',
        'rounded-l-none': props.prefix,
        'bg-gray-100 dark:!bg-sidebar dark:!text-muted-foreground':
          props.readonly || props.disabled,
      }"
      data-lpignore="true"
      :type="type || 'text'"
      :placeholder="placeholder"
      :readonly="readonly"
      :required="required"
      :pattern="pattern"
      :autocomplete="autocomplete"
      :title="title"
      :disabled="disabled"
      :name="name"
      v-bind="$attrs"
    />
    <div
      v-if="state?.status !== 'error'"
      :class="{
        'absolute right-3 flex space-x-1 transition-opacity peer-focus:opacity-0': true,
        // We hide inset buttons when the input is focused to make sure nothing occludes the full input
        // whilst the author is writing; however, authors might still _click_ that part of the input where
        // the button is hidden (esp. on mobile, to move the cursor), so we disable pointer events to prevent
        // the button from being clicked and Bad Things from happening.
        'peer-focus:pointer-events-none': true,
      }"
    >
      <slot name="icon" />
      <InsetButton v-if="copyable" @click="copyToClipboard">Copy</InsetButton>
    </div>
    <div v-if="state?.status === 'pending'" class="absolute right-3">
      <PendingIndicator pending />
    </div>
    <div v-if="state?.status === 'success'" class="absolute right-3">
      <CheckCircleIcon class="size-5 text-green-500" />
    </div>
    <div
      v-if="state?.status === 'error'"
      class="absolute right-3 flex items-center"
    >
      <Tooltip.Root interactive>
        <Tooltip.Trigger>
          <ExclamationCircleIcon class="size-5 text-red-500" />
        </Tooltip.Trigger>

        <Tooltip.Content>
          {{ state.message }}
          <RouterLink
            v-if="state.route"
            :to="state.route"
            class="mt-2 block cursor-pointer rounded bg-blue-500 px-2 py-1 text-xs font-bold uppercase text-white"
          >
            Click here
          </RouterLink>
        </Tooltip.Content>
      </Tooltip.Root>
    </div>
  </div>
</template>

<script lang="ts" setup>
import { type InputTypeHTMLAttribute, onMounted, useTemplateRef } from "vue";

import { useSettingControl } from "@/components/Setting/context";
import InsetButton from "@/components/Utilities/Forms/InsetButton.vue";
import PendingIndicator from "@/components/Utilities/PendingIndicator.vue";
import Tooltip from "@/design_system/Tooltip";
import CheckCircleIcon from "@/icons/heroicons/check-circle-mini.svg";
import ExclamationCircleIcon from "@/icons/heroicons/exclamation-circle-mini.svg";
import { showToast } from "@/lib/toasts";
import type { RouteLocation } from "@/router/types";

export type ValidationStatus =
  | {
      status: "pending" | "success";
    }
  | {
      status: "error";
      message: string;
      route?: RouteLocation;
    };

defineOptions({
  inheritAttrs: false,
});

const modelValue = defineModel<string>({ default: "" });

const props = defineProps<{
  id?: string;
  prefix?: string;
  state?: ValidationStatus | null;
  copyable?: boolean;
  autofocus?: boolean;
  autocomplete?: string;
  type?: InputTypeHTMLAttribute;
  placeholder?: string;
  readonly?: boolean;
  required?: boolean;
  title?: string;
  name?: string;
  pattern?: string;
  disabled?: boolean;
}>();

const input = useTemplateRef("input");

const setting = useSettingControl();
const settingId = setting?.register();

const copyToClipboard = () => {
  navigator.clipboard.writeText(modelValue.value || "");

  showToast({
    type: "inform",
    title: `Copied to clipboard`,
  });
};

onMounted(() => {
  if (props.autofocus) {
    input.value?.focus({ preventScroll: true });
  }
});
</script>
